import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaBan,
  FaCalendarCheck,
  FaChartLine,
  FaCheck,
  FaClock,
  FaDownload,
  FaExclamationTriangle,
  FaFileCsv,
  FaMoneyBillWave,
  FaRedo,
  FaSave,
  FaShieldAlt,
  FaSpinner,
  FaUserClock,
  FaUserFriends,
  FaUserTie,
  FaUsers,
} from 'react-icons/fa';
import {
  applyForfeoReserveMerchantPortalCommand,
  createForfeoReserveMerchantPortalCommand,
  createForfeoReserveMerchantPortalWindow,
  exportForfeoReserveMerchantPortal,
  fetchForfeoReserveMerchantPortalSnapshot,
} from '../../lib/forfeoReserveMerchantPortalClient';
import { humanizeForfeoReserveError } from '../../lib/forfeoReserveEdgeError';
import '../../styles/commerce-reserve-v21.css';
import type {
  ForfeoReserveMerchantPortalBooking,
  ForfeoReserveMerchantPortalCustomerProfile,
  ForfeoReserveMerchantPortalExportKind,
  ForfeoReserveMerchantPortalSnapshot,
} from '../../../supabase/functions/_shared/forfeo-reserve-merchant-portal';

type Props = {
  businessId: string;
  businessName?: string;
  onUpdate?: () => void | Promise<void>;
};

type Tab = 'overview' | 'bookings' | 'resources' | 'customers' | 'finance';

const EMPTY_METRICS = {
  catalogCount: 0,
  resourceCount: 0,
  employeeCount: 0,
  activeClosureCount: 0,
  bookingCount: 0,
  confirmedBookingCount: 0,
  cancelledBookingCount: 0,
  noShowBookingCount: 0,
  occupiedPartyUnits: 0,
  activeCapacityUnits: 0,
  operationalOccupancyPercent: 0,
  waitlistCount: 0,
  openReconciliationCount: 0,
};

function dateWindow(days: number) {
  const startsAt = new Date();
  startsAt.setHours(0, 0, 0, 0);
  const endsAt = new Date(startsAt.getTime() + days * 86_400_000);
  return { startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString() };
}

function formatDate(value?: string) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('fr-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatMoney(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function stateClass(state: string) {
  if (['confirmed', 'completed', 'resolved', 'active'].includes(state)) {
    return 'bg-emerald-100 text-emerald-800';
  }
  if (['cancelled', 'failed', 'expired', 'no_show', 'dead_letter'].includes(state)) {
    return 'bg-rose-100 text-rose-800';
  }
  if (['manual_review', 'acknowledged', 'partially_refunded'].includes(state)) {
    return 'bg-amber-100 text-amber-800';
  }
  return 'bg-slate-100 text-slate-700';
}

function downloadText(filename: string, mimeType: string, content: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function hashCustomerReference(businessId: string, reference: string) {
  const bytes = new TextEncoder().encode(`${businessId}:${reference}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

function MetricCard({
  icon,
  label,
  value,
  note,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  note?: string;
}) {
  return (
    <article className="reserve-portal-v213__metric rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 text-2xl text-blue-600">{icon}</div>
      <p className="text-xs font-black uppercase tracking-widest text-slate-700">{label}</p>
      <p className="mt-1 text-3xl font-black text-slate-950">{value}</p>
      {note && <p className="mt-2 text-xs text-slate-700">{note}</p>}
    </article>
  );
}

export default function ReserveMerchantPortal({
  businessId,
  businessName = 'Forfeo',
  onUpdate,
}: Props) {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState<Tab>('overview');
  const [snapshot, setSnapshot] = useState<ForfeoReserveMerchantPortalSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState<ForfeoReserveMerchantPortalExportKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [customerForm, setCustomerForm] = useState({
    customerReference: '',
    displayName: '',
    locale: 'fr-CA' as 'fr-CA' | 'en-CA' | 'fr' | 'en',
    preferenceTags: '',
    operationalNote: '',
  });

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
      const window = dateWindow(days);
      const request = createForfeoReserveMerchantPortalWindow({
        businessId,
        ...window,
        pageSize: 100,
      });
      const next = await fetchForfeoReserveMerchantPortalSnapshot(request);
      setSnapshot(next);
    } catch (caught) {
      setError(humanizeForfeoReserveError(caught instanceof Error ? caught.message : 'portal_snapshot_failed'));
    } finally {
      setLoading(false);
    }
  }, [businessId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const metrics = snapshot?.metrics || EMPTY_METRICS;
  const customerBookings = useMemo(
    () => (snapshot?.bookings || []).filter((booking) => booking.customerReference),
    [snapshot?.bookings],
  );

  const selectCustomer = (booking: ForfeoReserveMerchantPortalBooking) => {
    setCustomerForm((current) => ({
      ...current,
      customerReference: booking.customerReference || '',
      displayName: booking.customerLabel === 'Client Reserve'
        ? ''
        : booking.customerLabel,
    }));
    setTab('customers');
  };

  const saveCustomer = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const referenceHash = await hashCustomerReference(
        businessId,
        customerForm.customerReference.trim(),
      );
      const existing: ForfeoReserveMerchantPortalCustomerProfile | undefined =
        snapshot?.customerProfiles.find(
          (profile) => profile.customerReferenceHash === referenceHash,
        );
      const command = createForfeoReserveMerchantPortalCommand({
        kind: 'upsert_customer_profile',
        businessId,
        expectedRevision: existing?.revision || 0,
        customerProfile: {
          customerReference: customerForm.customerReference.trim(),
          displayName: customerForm.displayName.trim(),
          locale: customerForm.locale,
          preferenceTags: customerForm.preferenceTags
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
          operationalNote: customerForm.operationalNote.trim() || undefined,
        },
      });
      await applyForfeoReserveMerchantPortalCommand(command);
      setSuccess(existing ? 'Fiche client mise à jour.' : 'Fiche client créée.');
      await load();
      await onUpdate?.();
    } catch (caught) {
      setError(humanizeForfeoReserveError(caught instanceof Error ? caught.message : 'customer_profile_failed'));
    } finally {
      setSaving(false);
    }
  };

  const updateReconciliation = async (
    caseId: string,
    kind: 'acknowledge_reconciliation_case' | 'resolve_reconciliation_case',
  ) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const command = createForfeoReserveMerchantPortalCommand({
        kind,
        businessId,
        reconciliationCaseId: caseId,
      });
      await applyForfeoReserveMerchantPortalCommand(command);
      setSuccess(
        kind === 'resolve_reconciliation_case'
          ? 'Dossier de rapprochement résolu.'
          : 'Dossier de rapprochement pris en charge.',
      );
      await load();
      await onUpdate?.();
    } catch (caught) {
      setError(humanizeForfeoReserveError(caught instanceof Error ? caught.message : 'reconciliation_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const runExport = async (exportKind: ForfeoReserveMerchantPortalExportKind) => {
    setExporting(exportKind);
    setError(null);
    try {
      const window = dateWindow(days);
      const request = createForfeoReserveMerchantPortalWindow({
        businessId,
        ...window,
        pageSize: 100,
      });
      const result = await exportForfeoReserveMerchantPortal({
        ...request,
        exportKind,
        rowLimit: 500,
      });
      downloadText(result.filename, result.mimeType, result.content);
      setSuccess(`Export ${exportKind} généré (${result.rowCount} lignes).`);
    } catch (caught) {
      setError(humanizeForfeoReserveError(caught instanceof Error ? caught.message : 'portal_export_failed'));
    } finally {
      setExporting(null);
    }
  };

  if (loading && !snapshot) {
    return (
      <section className="rounded-[2rem] border border-slate-200 bg-white p-10 text-center shadow-sm" data-commerce-panel="reserve-portal">
        <FaSpinner className="mx-auto mb-4 animate-spin text-4xl text-indigo-600" />
        <p className="font-bold text-slate-600">Chargement du portail Reserve...</p>
      </section>
    );
  }

  return (
    <section className="reserve-portal-v213 reserve-portal-v214 space-y-6" data-forfeo-reserve-merchant-portal data-forfeo-ui-v218="reserve-portal" data-commerce-panel-v219="reserve-portal" data-commerce-v220-panel="reserve-portal">
      <header className="reserve-portal-v213__hero overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-5 text-slate-950 shadow-sm md:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.3em] text-blue-600">
              Forfeo Reserve · autorité opérationnelle
            </p>
            <h2 className="mt-2 text-3xl font-black md:text-4xl">Portail marchand</h2>
            <p className="mt-2 max-w-3xl text-sm text-slate-700">
              {businessName} · réservations, ressources, clients pseudonymisés,
              occupation et rapprochements. Les revenus sont lus exclusivement depuis Giftshop.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {[7, 30, 90].map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={days === value}
                data-forfeo-v218-range-pill
                onClick={() => setDays(value)}
                className={`rounded-full px-4 py-2 text-sm font-black transition ${
                  days === value
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'border border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                }`}
              >
                {value} j
              </button>
            ))}
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
            >
              <FaRedo className={loading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
        </div>
      </header>

      <div className="reserve-portal-v213__boundary rounded-3xl border border-blue-100 bg-blue-50/70 p-4 text-sm text-slate-700">
        <div className="flex gap-3">
          <FaShieldAlt className="mt-1 shrink-0 text-indigo-600" />
          <p>
            <strong>Frontière officielle :</strong> Reserve fournit les données opérationnelles.
            Giftshop fournit les montants officiels. Le portail ne recalcule aucun prix,
            aucune taxe, aucun remboursement et ne montre aucun identifiant Stripe.
          </p>
        </div>
      </div>

      {(error || success) && (
        <div className={`rounded-2xl border p-4 text-sm font-bold ${
          error
            ? 'border-rose-200 bg-rose-50 text-rose-800'
            : 'border-emerald-200 bg-emerald-50 text-emerald-800'
        }`}>
          {error || success}
        </div>
      )}

      <nav className="reserve-portal-v213__tabs flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {([
          ['overview', 'Vue d’ensemble'],
          ['bookings', 'Réservations'],
          ['resources', 'Ressources'],
          ['customers', 'Clients'],
          ['finance', 'Giftshop & rapprochements'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`whitespace-nowrap rounded-xl px-4 py-3 text-sm font-black transition ${
              tab === value
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-blue-50 hover:text-blue-700'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              icon={<FaCalendarCheck />}
              label="Réservations"
              value={metrics.bookingCount}
              note={`${metrics.confirmedBookingCount} confirmées · ${metrics.cancelledBookingCount} annulées`}
            />
            <MetricCard
              icon={<FaUsers />}
              label="Occupation enregistrée"
              value={`${metrics.operationalOccupancyPercent}%`}
              note={`${metrics.occupiedPartyUnits} unités réservées · capacité active ${metrics.activeCapacityUnits}`}
            />
            <MetricCard
              icon={<FaUserTie />}
              label="Ressources & employés"
              value={metrics.resourceCount}
              note={`${metrics.employeeCount} ressources de type employé`}
            />
            <MetricCard
              icon={<FaExclamationTriangle />}
              label="À traiter"
              value={metrics.openReconciliationCount}
              note={`${metrics.noShowBookingCount} absences · ${metrics.waitlistCount} en attente`}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-xl font-black text-slate-950">Prochaines réservations</h3>
                <button type="button" onClick={() => setTab('bookings')} className="text-sm font-black text-indigo-600">
                  Tout voir
                </button>
              </div>
              <div className="space-y-3">
                {(snapshot?.bookings || []).slice(0, 6).map((booking) => (
                  <button
                    key={booking.id}
                    type="button"
                    onClick={() => selectCustomer(booking)}
                    className="flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-100 p-4 text-left hover:border-indigo-200 hover:bg-indigo-50/40"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-black text-slate-900">{booking.catalogItemName}</p>
                      <p className="truncate text-sm text-slate-700">{booking.customerLabel} · {formatDate(booking.startsAt)}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-black ${stateClass(booking.state)}`}>
                      {booking.state}
                    </span>
                  </button>
                ))}
                {(snapshot?.bookings || []).length === 0 && (
                  <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-700">
                    Aucune réservation dans cette fenêtre.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-xl font-black text-slate-950">État opérationnel</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-slate-50 p-4"><FaBan className="mb-2 text-rose-500" /><p className="text-2xl font-black">{metrics.activeClosureCount}</p><p className="text-xs font-bold text-slate-700">fermetures actives</p></div>
                <div className="rounded-2xl bg-slate-50 p-4"><FaUserClock className="mb-2 text-amber-500" /><p className="text-2xl font-black">{metrics.waitlistCount}</p><p className="text-xs font-bold text-slate-700">demandes en attente</p></div>
                <div className="rounded-2xl bg-slate-50 p-4"><FaUserFriends className="mb-2 text-blue-500" /><p className="text-2xl font-black">{snapshot?.customerProfiles.length || 0}</p><p className="text-xs font-bold text-slate-700">fiches pseudonymisées</p></div>
                <div className="rounded-2xl bg-slate-50 p-4"><FaClock className="mb-2 text-indigo-500" /><p className="text-2xl font-black">{metrics.catalogCount}</p><p className="text-xs font-bold text-slate-700">éléments réservables</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'bookings' && (
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div><h3 className="text-2xl font-black text-slate-950">Réservations Reserve</h3><p className="text-sm text-slate-700">Fenêtre de {days} jours · capacité et états issus de Reserve.</p></div>
            <button type="button" onClick={() => void runExport('bookings')} disabled={exporting !== null} className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-black text-slate-900 disabled:opacity-50"><FaFileCsv /> Export CSV</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] text-left text-sm">
              <thead><tr className="border-b text-xs uppercase tracking-wider text-slate-700"><th className="py-3">Service</th><th>Client</th><th>Début</th><th>Fin</th><th>Groupe</th><th>État</th><th>Action</th></tr></thead>
              <tbody>
                {(snapshot?.bookings || []).map((booking) => (
                  <tr key={booking.id} className="border-b last:border-0">
                    <td className="py-4 font-black text-slate-900">{booking.catalogItemName}</td>
                    <td>{booking.customerLabel}</td>
                    <td>{formatDate(booking.startsAt)}</td>
                    <td>{formatDate(booking.endsAt)}</td>
                    <td>{booking.partySize}</td>
                    <td><span className={`rounded-full px-3 py-1 text-xs font-black ${stateClass(booking.state)}`}>{booking.state}</span></td>
                    <td><button type="button" onClick={() => selectCustomer(booking)} disabled={!booking.customerReference} className="rounded-lg bg-indigo-50 px-3 py-2 text-xs font-black text-indigo-700 disabled:opacity-40">Fiche client</button></td>
                  </tr>
                ))}
                {(snapshot?.bookings || []).length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-700">Aucune réservation.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'resources' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-xl font-black text-slate-950">Ressources et employés</h3>
            <div className="space-y-3">
              {(snapshot?.resources || []).map((resource) => (
                <div key={resource.id} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 p-4">
                  <div><p className="font-black text-slate-900">{resource.name}</p><p className="text-xs text-slate-700">{resource.resourceKind} · {resource.timezone}</p></div>
                  <div className="text-right"><p className="font-black text-slate-900">{resource.capacityTotal} {resource.capacityUnit}</p><span className={`rounded-full px-2 py-1 text-[10px] font-black ${stateClass(resource.status)}`}>{resource.status}</span></div>
                </div>
              ))}
              {(snapshot?.resources || []).length === 0 && <p className="rounded-2xl bg-slate-50 p-6 text-center text-slate-700">Aucune ressource.</p>}
            </div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-xl font-black text-slate-950">Fermetures et blocages</h3>
            <div className="space-y-3">
              {(snapshot?.closures || []).map((closure) => (
                <div key={closure.id} className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
                  <p className="font-black text-rose-900">{closure.reasonCode || 'Période bloquée'}</p>
                  <p className="mt-1 text-sm text-rose-700">{formatDate(closure.startsAt)} → {formatDate(closure.endsAt)}</p>
                  <p className="mt-1 text-xs text-rose-600">{closure.targetType}</p>
                </div>
              ))}
              {(snapshot?.closures || []).length === 0 && <p className="rounded-2xl bg-emerald-50 p-6 text-center text-emerald-700">Aucune fermeture active dans cette fenêtre.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === 'customers' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-xl font-black text-slate-950">Fiche client — référence pseudonymisée</h3>
            <p className="mt-1 text-sm text-slate-700">Sélectionne une réservation. La référence brute n’est jamais stockée; seul son SHA-256 est conservé.</p>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-black text-slate-700">Réservation client
                <select value={customerForm.customerReference} onChange={(event) => {
                  const booking = customerBookings.find((item) => item.customerReference === event.target.value);
                  setCustomerForm((current) => ({ ...current, customerReference: event.target.value, displayName: booking?.customerLabel === 'Client Reserve' ? current.displayName : booking?.customerLabel || current.displayName }));
                }} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3">
                  <option value="">Choisir une référence</option>
                  {customerBookings.map((booking) => <option key={booking.id} value={booking.customerReference}>{booking.customerLabel} · {formatDate(booking.startsAt)}</option>)}
                </select>
              </label>
              <label className="block text-sm font-black text-slate-700">Nom d’affichage
                <input value={customerForm.displayName} onChange={(event) => setCustomerForm((current) => ({ ...current, displayName: event.target.value }))} maxLength={160} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3" placeholder="Client fidèle" />
              </label>
              <label className="block text-sm font-black text-slate-700">Langue
                <select value={customerForm.locale} onChange={(event) => setCustomerForm((current) => ({ ...current, locale: event.target.value as typeof current.locale }))} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3"><option value="fr-CA">Français (Canada)</option><option value="en-CA">English (Canada)</option><option value="fr">Français</option><option value="en">English</option></select>
              </label>
              <label className="block text-sm font-black text-slate-700">Préférences
                <input value={customerForm.preferenceTags} onChange={(event) => setCustomerForm((current) => ({ ...current, preferenceTags: event.target.value }))} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3" placeholder="terrasse, végétarien, matin" />
              </label>
              <label className="block text-sm font-black text-slate-700">Note opérationnelle
                <textarea value={customerForm.operationalNote} onChange={(event) => setCustomerForm((current) => ({ ...current, operationalNote: event.target.value }))} maxLength={1000} rows={4} className="mt-2 w-full resize-none rounded-xl border border-slate-200 px-4 py-3" placeholder="Aucun courriel, téléphone, donnée médicale ou information de paiement." />
              </label>
              <button type="button" onClick={() => void saveCustomer()} disabled={saving || !customerForm.customerReference || !customerForm.displayName.trim()} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-black text-white hover:bg-indigo-500 disabled:opacity-50"><FaSave /> {saving ? 'Enregistrement...' : 'Enregistrer la fiche'}</button>
            </div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3"><h3 className="text-xl font-black text-slate-950">Fiches existantes</h3><button type="button" onClick={() => void runExport('customers')} disabled={exporting !== null} className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-black text-slate-900"><FaDownload /> Export</button></div>
            <div className="space-y-3">
              {(snapshot?.customerProfiles || []).map((profile) => (
                <article key={profile.id} className="rounded-2xl border border-slate-100 p-4"><div className="flex items-start justify-between gap-3"><div><p className="font-black text-slate-900">{profile.displayName}</p><p className="text-xs text-slate-700">{profile.locale} · révision {profile.revision}</p></div><span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-black text-indigo-700">{profile.preferenceTags.length} préférences</span></div>{profile.preferenceTags.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{profile.preferenceTags.map((tag) => <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{tag}</span>)}</div>}{profile.operationalNote && <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{profile.operationalNote}</p>}</article>
              ))}
              {(snapshot?.customerProfiles || []).length === 0 && <p className="rounded-2xl bg-slate-50 p-8 text-center text-slate-700">Aucune fiche client.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === 'finance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {(snapshot?.financial.currencyTotals || []).map((total) => (
              <MetricCard key={total.currency} icon={<FaMoneyBillWave />} label={`Confirmé · ${total.currency.toUpperCase()}`} value={formatMoney(total.confirmedCents, total.currency)} note={`${total.confirmedCount} bindings confirmés · données Giftshop`} />
            ))}
            {snapshot?.financial.available === false && (
              <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 text-amber-900 md:col-span-3"><FaExclamationTriangle className="mb-2 text-2xl" /><p className="font-black">Lecture Giftshop indisponible</p><p className="text-sm">Aucun montant de remplacement n’est calculé dans Reserve. Code : {snapshot.financial.errorCode || 'unavailable'}</p></div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><div><h3 className="text-xl font-black text-slate-950">Rapprochements à traiter</h3><p className="text-sm text-slate-700">Les actions changent seulement le suivi opérationnel dans Reserve; elles ne remboursent et ne capturent aucun paiement.</p></div><button type="button" onClick={() => void runExport('reconciliation')} disabled={exporting !== null} className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-black text-slate-900"><FaFileCsv /> Export CSV</button></div>
            <div className="space-y-3">
              {(snapshot?.reconciliationCases || []).map((entry) => (
                <article key={entry.id} className="rounded-2xl border border-slate-100 p-4"><div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"><div><p className="font-black text-slate-900">{entry.reason}</p><p className="mt-1 text-sm text-slate-700">Créé {formatDate(entry.createdAt)} · dossier {entry.id.slice(0, 8)}</p><span className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-black ${stateClass(entry.state)}`}>{entry.state}</span></div><div className="flex flex-wrap gap-2">{entry.state === 'open' && <button type="button" disabled={saving} onClick={() => void updateReconciliation(entry.id, 'acknowledge_reconciliation_case')} className="rounded-xl bg-amber-100 px-4 py-2 text-sm font-black text-amber-900">Prendre en charge</button>}{entry.state !== 'resolved' && <button type="button" disabled={saving} onClick={() => void updateReconciliation(entry.id, 'resolve_reconciliation_case')} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-black text-white"><FaCheck /> Marquer résolu</button>}</div></div></article>
              ))}
              {(snapshot?.reconciliationCases || []).length === 0 && <p className="rounded-2xl bg-emerald-50 p-8 text-center font-bold text-emerald-700">Aucun dossier de rapprochement.</p>}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 flex items-center gap-2 text-xl font-black text-slate-950"><FaChartLine className="text-emerald-600" /> Activité financière officielle</h3>
            <div className="overflow-x-auto"><table className="w-full min-w-[760px] text-left text-sm"><thead><tr className="border-b text-xs uppercase text-slate-700"><th className="py-3">Date</th><th>Mode</th><th>Montant Giftshop</th><th>État</th></tr></thead><tbody>{(snapshot?.financial.bindings || []).map((binding) => <tr key={binding.bindingId} className="border-b last:border-0"><td className="py-3">{formatDate(binding.createdAt)}</td><td className="font-bold">{binding.confirmationMode}</td><td className="font-black text-emerald-700">{formatMoney(binding.totalCents, binding.currency)}</td><td><span className={`rounded-full px-3 py-1 text-xs font-black ${stateClass(binding.state)}`}>{binding.state}</span></td></tr>)}{(snapshot?.financial.bindings || []).length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-700">Aucun binding financier dans cette fenêtre.</td></tr>}</tbody></table></div>
          </div>
        </div>
      )}
    </section>
  );
}
